import { decodeHTML } from 'entities'
import sanitize from 'sanitize-html'

/** Escapes HTML special characters to prevent injection in rendered templates. */
export function escapeHtml(str: string): string {
  return str
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;')
}

const richTextConfig: sanitize.IOptions = {
  allowedTags: ['p', 'b', 'strong', 'i', 'em', 'ul', 'ol', 'li', 'br', 'a', 'span'],
  allowedAttributes: {
    a: ['href', 'target', 'rel'],
    li: ['value'],
    span: ['style'],
  },
  allowedStyles: {
    span: { color: [/^#[0-9a-fA-F]{3,8}$/, /^rgb\(\s*\d+,\s*\d+,\s*\d+\s*\)$/] },
  },
}

/** Sanitise an HTML string using an allowlist suitable for rich text content. */
export function sanitiseRichTextHtml(html: string): string {
  return sanitize(html, richTextConfig)
}

/**
 * Strip all HTML tags and decode entities, returning plain text.
 * Pass `{ trim: false }` to preserve leading/trailing whitespace — useful
 * when extracting inline segments where surrounding spaces are significant.
 *
 * sanitize-html re-encodes `<`, `>`, `&` on output, so we run a final
 * `decodeHTML` pass to produce plain text suitable for non-HTML renderers
 * (e.g. react-pdf, where literal `&gt;` would otherwise be shown verbatim).
 */
export function stripHtmlToText(html: string, options?: { trim?: boolean }): string {
  const withNewlines = html.replace(/<\/(p|li|tr|br|div|h[1-6])\s*>/gi, '\n')
  const sanitised = sanitize(withNewlines, { allowedTags: [], allowedAttributes: {} }).replace(
    /\n{3,}/g,
    '\n\n'
  )
  const result = decodeHTML(sanitised)
  return options?.trim === false ? result : result.trim()
}

/**
 * Check whether an HTML string is effectively empty — no visible text content.
 * Returns true for empty strings, whitespace-only strings, strings containing
 * only empty HTML tags (e.g. `<p></p>`, `<p><br></p>`), and strings whose
 * only content is HTML entities that decode to whitespace (e.g. `&#160;`).
 */
export function isEmptyHtml(html: string | undefined | null): boolean {
  if (!html) return true
  const stripped = html
    .replace(/<[^>]*>/g, '')
    .replace(/&(#\d+|#x[0-9a-f]+|[a-z]+);/gi, '')
    .trim()
  return stripped.length === 0
}
